// MyDialogButton.tsx
import React, { useState, useCallback, useEffect } from 'react';
import FullScreen from './FullScreen';
import { FaTimes } from 'react-icons/fa';

interface MyDialogButtonProps {
  label: string;
  dialogHeader?: string;
  dialogContent: React.ReactNode;
  buttonClassName?: string;
  dialogClassName?: string;
  fullScreen?: boolean;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  variant?: 'modal' | 'fullscreen' | 'inline';
  trigger?: 'click' | 'hover';
}

export const MyDialogButton = ({
  label,
  dialogHeader,
  dialogContent,
  buttonClassName = '',
  dialogClassName = '',
  fullScreen = false,
  closeOnBackdrop = true,
  showCloseButton = true,
  variant = 'modal',
  trigger = 'click'
}: MyDialogButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (variant === 'fullscreen') {
    return (
      <>
        <button
          onClick={handleOpen}
          className={`px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium text-sm ${buttonClassName}`}
        >
          {label}
        </button>

        {isOpen && (
          <FullScreen
            isOpen={isOpen}
            onClose={handleClose}
            closeOnBackdrop={closeOnBackdrop}
            showCloseButton={showCloseButton}
            className={dialogClassName}
          >
            <div className="p-6">
              {dialogHeader && (
                <h2 className="text-2xl font-bold text-gray-800 mb-6">{dialogHeader}</h2>
              )}
              {dialogContent}
            </div>
          </FullScreen>
        )}
      </>
    );
  }

  // Modal variant - centered overlay with fixed size
  return (
    <>
      <button
        onClick={handleOpen}
        onMouseEnter={trigger === 'hover' ? handleOpen : undefined}
        onMouseLeave={trigger === 'hover' ? handleClose : undefined}
        className={`px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium text-sm ${buttonClassName}`}
      >
        {label}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (closeOnBackdrop && e.target === e.currentTarget) {
              handleClose();
            }
          }}
        >
          <div className={`
            bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto
            animate-fade-in relative
            ${dialogClassName}
          `}>
            {/* Close button */}
            {showCloseButton && (
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors z-10"
              >
                <FaTimes className="text-xl" />
              </button>
            )}

            <div className="p-6">
              {dialogHeader && (
                <h2 className="text-2xl font-bold text-gray-800 mb-6 pr-8">{dialogHeader}</h2>
              )}
              {dialogContent}
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
      `}</style>
    </>
  );
};